'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Phone, Image, MessageCircle, ChevronRight, Clock, Calendar, Activity, PhoneCall } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import { ANDRE_PHOTO_URL, formatLastCallLabel } from '@/lib/familyDemoData';
import { useFamilyDemo } from '@/lib/familyDemoState';
import { toast } from 'sonner';

export default function FamilyDashboard() {
  const router = useRouter();
  const { state, handleNotification, deferNotification } = useFamilyDemo();
  const { seniorProfile, notifications, activities, agendaEvents, callRecords } = state;

  const [callbackHandled, setCallbackHandled] = useState(false);
  const [lastCallLabel, setLastCallLabel] = useState('Aucun appel récent');

  const activeCallback = notifications.find(
    (n) => n.type === 'callback_request' && n.status !== 'handled' && !callbackHandled
  );

  // Sort events by isoDate
  const sortedEvents = [...agendaEvents].sort((a, b) => {
    const aIso = a.isoDate ?? '';
    const bIso = b.isoDate ?? '';
    return aIso.localeCompare(bIso) || (a.time ?? '').localeCompare(b.time ?? '');
  });
  const nextEvent = sortedEvents[0];

  const recentActivities = activities.slice(0, 4);

  // Last completed call from callRecords
  const lastCall = callRecords.find((c) => c.status === 'completed');

  // Compute last call display on client only to avoid hydration mismatch
  useEffect(() => {
    const label = lastCall?.startedAt
      ? `Dernier appel : ${formatLastCallLabel(lastCall.startedAt)}`
      : seniorProfile.lastCallAt
      ? `Dernier appel : ${formatLastCallLabel(seniorProfile.lastCallAt)}`
      : 'Aucun appel récent';
    setLastCallLabel(label);
  }, [lastCall, seniorProfile.lastCallAt]);

  const handleCallAndre = () => {
    router.push('/outgoing-call-screen');
  };

  const handleSendPhoto = () => {
    router.push('/send-screen?action=photo');
  };

  const handleSendMessage = () => {
    router.push('/messages');
  };

  const handleRappeler = () => {
    if (activeCallback) {
      handleNotification(activeCallback.id, 'read');
      setCallbackHandled(true);
      router.push(`/outgoing-call-screen?notifId=${activeCallback.id}`);
    } else {
      router.push('/outgoing-call-screen');
    }
  };

  const handlePlusTard = () => {
    if (activeCallback) {
      deferNotification(activeCallback.id);
      setCallbackHandled(true);
      toast('Rappel différé', {
        description: "L'alerte reste visible dans les Alertes.",
        duration: 3000,
      });
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'call': return <Phone size={14} className="text-lucat-blue" />;
      case 'photo': return <Image size={14} className="text-lucat-teal" />;
      case 'message': return <MessageCircle size={14} className="text-lucat-purple" />;
      case 'quiz': return <Activity size={14} className="text-lucat-purple" />;
      case 'visit': case'agenda': return <Calendar size={14} className="text-lucat-green" />;
      default: return <Clock size={14} className="text-muted-foreground" />;
    }
  };

  const formatDur = (secs: number) => {
    if (secs < 60) return `${secs} seconde${secs > 1 ? 's' : ''}`;
    const m = Math.floor(secs / 60);
    const rem = secs % 60;
    return rem > 0 ? `${m} min ${rem} sec` : `${m} minute${m > 1 ? 's' : ''}`;
  };

  const lastCallDuration = lastCall?.durationSeconds
    ? formatDur(lastCall.durationSeconds)
    : seniorProfile.lastCallDuration ?? '';

  return (
    <div className="pb-4">
      {/* André Hero Card */}
      <div className="bg-lucat-navy px-4 pt-4 pb-6">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-20 h-20 rounded-2xl overflow-hidden border-3 border-lucat-green/50 flex-shrink-0" style={{ borderWidth: '3px' }}>
              <AppImage
                src={ANDRE_PHOTO_URL}
                alt="André Beaumont, senior, homme âgé souriant, portrait chaleureux"
                width={80}
                height={80}
                priority
                className="w-full h-full object-cover object-top"
              />
            </div>
            <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-lucat-green rounded-full border-2 border-lucat-navy flex items-center justify-center">
              <span className="w-2 h-2 bg-white rounded-full" />
            </span>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-white text-2xl font-bold leading-tight">{seniorProfile.firstName}</h1>
            <p className="text-white/70 text-sm font-medium mt-0.5">Mon père</p>
            <div className="mt-2 flex flex-col gap-1">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-lucat-green rounded-full flex-shrink-0" />
                <span className="text-white/80 text-xs">{seniorProfile.connectionStatus}</span>
              </div>
              <div className="bg-lucat-green/20 rounded-lg px-2.5 py-1 inline-flex items-center gap-1.5 self-start">
                <span className="text-lucat-green text-xs font-semibold">{seniorProfile.socialStatus}</span>
              </div>
            </div>
          </div>
        </div>

        {/* 3 Main Action Buttons */}
        <div className="grid grid-cols-3 gap-2.5 mt-5">
          <button
            onClick={handleCallAndre}
            className="flex flex-col items-center gap-1.5 bg-lucat-green text-white rounded-2xl py-3.5 px-2 tap-target active:scale-95 transition-all duration-150 shadow-card"
          >
            <Phone size={22} strokeWidth={2} />
            <span className="text-xs font-bold text-center leading-tight">Appeler<br />André</span>
          </button>

          <button
            onClick={handleSendPhoto}
            className="flex flex-col items-center gap-1.5 bg-white/15 text-white rounded-2xl py-3.5 px-2 tap-target active:scale-95 transition-all duration-150"
          >
            <Image size={22} strokeWidth={2} />
            <span className="text-xs font-bold text-center leading-tight">Envoyer<br />une photo</span>
          </button>

          <button
            onClick={handleSendMessage}
            className="flex flex-col items-center gap-1.5 bg-white/15 text-white rounded-2xl py-3.5 px-2 tap-target active:scale-95 transition-all duration-150"
          >
            <MessageCircle size={22} strokeWidth={2} />
            <span className="text-xs font-bold text-center leading-tight">Envoyer<br />un message</span>
          </button>
        </div>
      </div>

      <div className="px-4 pt-4 flex flex-col gap-3">

        {/* Callback Request Card */}
        {activeCallback && !callbackHandled && (
          <div className="bg-card rounded-2xl p-4 border border-lucat-orange/30 shadow-card bg-lucat-orange-light slide-up">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-lucat-orange flex items-center justify-center flex-shrink-0">
                <PhoneCall size={18} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-lucat-navy font-bold text-[15px]">{activeCallback.title}</p>
                <p className="text-muted-foreground text-sm mt-0.5">{activeCallback.description}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button
                onClick={handleRappeler}
                className="bg-lucat-green text-white rounded-xl py-2.5 px-3 text-sm font-bold tap-target active:scale-95 transition-all duration-150 flex items-center justify-center gap-1.5"
              >
                <Phone size={15} />
                Rappeler André
              </button>
              <button
                onClick={handlePlusTard}
                className="bg-white text-lucat-orange rounded-xl py-2.5 px-3 text-sm font-semibold tap-target active:scale-95 transition-all duration-150 border border-lucat-orange/30 flex items-center justify-center"
              >
                Plus tard
              </button>
            </div>
          </div>
        )}

        {/* Next Visit Card */}
        {nextEvent && (
          <div className="rounded-2xl p-4 shadow-card border" style={{ backgroundColor: '#F1F6FF', borderColor: '#CFE0FF' }}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#E3EEFF' }}>
                  <Calendar size={16} style={{ color: '#377EF0' }} />
                </div>
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#587196' }}>Prochaine visite</span>
              </div>
            </div>
            <p className="text-lucat-navy font-bold text-lg">{nextEvent.date} à {nextEvent.time}</p>
            <p className="text-muted-foreground text-sm">{nextEvent.title ?? nextEvent.label}</p>
            <button
              onClick={() => router.push('/agenda')}
              className="mt-3 w-full rounded-xl py-2.5 text-sm font-semibold tap-target active:scale-95 transition-all duration-150 flex items-center justify-center gap-1.5"
              style={{ backgroundColor: '#EAF2FF', color: '#2870E8' }}
            >
              <Calendar size={15} />
              Voir l&apos;agenda
            </button>
          </div>
        )}

        {/* Recent Activity Card */}
        <div className="rounded-2xl p-4 shadow-card border" style={{ backgroundColor: '#F0FAF8', borderColor: '#C9EBE4' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#E1F6F1' }}>
                <Activity size={16} style={{ color: '#169C88' }} />
              </div>
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#4F7D76' }}>Activité récente</span>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {recentActivities.map((act) => (
              <div key={act.id} className="flex items-center gap-2.5 py-1">
                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                  {getActivityIcon(act.type)}
                </div>
                <p className="text-foreground text-sm flex-1 leading-snug">{act.title ?? act.label}</p>
              </div>
            ))}
          </div>
          <button
            onClick={() => router.push('/activities')}
            className="mt-3 w-full flex items-center justify-center gap-1.5 text-sm font-semibold py-2 tap-target active:scale-95 transition-all duration-150"
            style={{ color: '#087F70' }}
          >
            Voir toutes les activités
            <ChevronRight size={15} />
          </button>
        </div>

        {/* Last Contact Card */}
        {(lastCall || seniorProfile.lastCallAt) && (
          <div className="rounded-2xl p-4 shadow-card border" style={{ backgroundColor: '#F3FBEF', borderColor: '#D3EDC6' }}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#E7F7DF' }}>
                <Phone size={16} style={{ color: '#45B817' }} />
              </div>
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#5E8050' }}>Dernier contact</span>
            </div>
            <p className="text-lucat-navy font-bold text-[15px]">{lastCallLabel}</p>
            {lastCallDuration && (
              <p className="text-muted-foreground text-sm mt-0.5 flex items-center gap-1.5">
                <Clock size={13} />
                Durée : {lastCallDuration}
              </p>
            )}
            <button
              onClick={handleCallAndre}
              className="mt-3 w-full rounded-xl py-2.5 text-sm font-semibold tap-target active:scale-95 transition-all duration-150 flex items-center justify-center gap-1.5"
              style={{ backgroundColor: '#E8F8DF', color: '#35A90D' }}
            >
              <Phone size={15} />
              Appeler à nouveau
            </button>
          </div>
        )}
      </div>
    </div>
  );
}